import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getAmountToPay, getAmountToReceive } from '@/features/exchange/lib';
import { useAssetsExchangeRates } from '../api/queries';
import { NUMERIC_CONFIG } from '@/shared/config';
import { applyAmountPrecision, formatReceivedPrice } from '@/shared/lib';
import Big from 'big.js';
import type { ExchangeAmountSource } from './types';

type UseExchangeConversionProps = {
  amountToPay: string;
  amountToReceive: string;
  assetCodeToPay: string;
  assetCodeToReceive: string;
  amountSource: ExchangeAmountSource;
  setExchangeRate: (rate: { leftPart: string; rightPart: string }) => void;
  setAmountToPay: (amount: string) => void;
  setAmountToReceive: (amount: string) => void;
  ratesRefetchInterval?: number;
};

const useExchangeConversion = ({
  amountToPay,
  amountToReceive,
  assetCodeToPay,
  assetCodeToReceive,
  amountSource,
  setExchangeRate,
  setAmountToPay,
  setAmountToReceive,
  ratesRefetchInterval,
}: UseExchangeConversionProps) => {
  const { t } = useTranslation();
  const {
    data: assetsExchangeRatesData,
    isLoading,
    isFetching,
    isError: isRatesError,
    error: ratesError,
  } = useAssetsExchangeRates(assetCodeToPay, assetCodeToReceive, true, {
    refetchInterval: ratesRefetchInterval,
  });
  const amountToPayRef = useRef(amountToPay);
  const amountToReceiveRef = useRef(amountToReceive);
  const amountSourceRef = useRef(amountSource);
  const pairKeyRef = useRef<string>('');
  const ratesDataRef = useRef(assetsExchangeRatesData);

  useEffect(() => {
    amountToPayRef.current = amountToPay;
  }, [amountToPay]);

  useEffect(() => {
    amountToReceiveRef.current = amountToReceive;
  }, [amountToReceive]);

  useEffect(() => {
    amountSourceRef.current = amountSource;
  }, [amountSource]);

  const recalculateAmountToReceive = useCallback(
    (amountToPay: string) => {
      if (!assetsExchangeRatesData || !assetCodeToPay || !assetCodeToReceive)
        return;
      if (!(Number(amountToPay) > 0)) {
        setAmountToReceive('');
        return;
      }
      const { symbol, priceBid, priceAsk, assetLeft, assetRight } =
        assetsExchangeRatesData;

      const assetToReceive =
        assetLeft.code === assetCodeToReceive ? assetLeft : assetRight;

      const amountToReceive = getAmountToReceive(
        symbol,
        assetCodeToPay,
        assetCodeToReceive,
        priceBid,
        priceAsk,
        amountToPay
      );

      const formatted = applyAmountPrecision(
        amountToReceive,
        assetToReceive.precision ?? NUMERIC_CONFIG.precision.default,
        'credit'
      );

      setAmountToReceive(new Big(formatted).toFixed());
    },
    [assetsExchangeRatesData, assetCodeToPay, assetCodeToReceive]
  );

  const recalculateAmountToPay = useCallback(
    (amountToReceive: string) => {
      if (!assetsExchangeRatesData || !assetCodeToPay || !assetCodeToReceive)
        return;
      if (!(Number(amountToReceive) > 0)) {
        setAmountToPay('');
        return;
      }
      const { symbol, priceBid, priceAsk, assetLeft, assetRight } =
        assetsExchangeRatesData;

      const assetToPay =
        assetLeft.code === assetCodeToPay ? assetLeft : assetRight;

      const amountToPay = getAmountToPay(
        symbol,
        assetCodeToPay,
        assetCodeToReceive,
        priceBid,
        priceAsk,
        amountToReceive
      );

      const formatted = applyAmountPrecision(
        amountToPay,
        assetToPay.precision ?? NUMERIC_CONFIG.precision.default,
        'credit'
      );

      setAmountToPay(new Big(formatted).toFixed());
    },
    [assetsExchangeRatesData, assetCodeToPay, assetCodeToReceive]
  );

  const exchangeRateDescription = useMemo(() => {
    if (!assetsExchangeRatesData || !assetCodeToPay || !assetCodeToReceive)
      return '';
    const { symbol, priceBid, priceAsk, displayRateAsk, displayRateBid } =
      assetsExchangeRatesData;

    let leftAsset = '';
    let rightAsset = '';
    let displayRate = 0;

    if (symbol === `${assetCodeToPay}${assetCodeToReceive}`) {
      leftAsset = assetCodeToPay;
      rightAsset = assetCodeToReceive;
      displayRate = displayRateBid ?? priceBid;
    } else if (symbol === `${assetCodeToReceive}${assetCodeToPay}`) {
      leftAsset = assetCodeToReceive;
      rightAsset = assetCodeToPay;
      displayRate = displayRateAsk ?? priceAsk;
    }

    const exchangeRateRoundedString = formatReceivedPrice(displayRate);

    const leftPart = `1 ${leftAsset}`;
    const rightPart = `${exchangeRateRoundedString} ${rightAsset}`;
    const description = `${t('exchange.exchangeRate')}: ${leftPart} ≈ ${rightPart}`;

    setExchangeRate({ leftPart, rightPart });

    return description;
  }, [
    assetsExchangeRatesData,
    assetCodeToPay,
    assetCodeToReceive,
    setExchangeRate,
    t,
  ]);

  useEffect(() => {
    if (!assetsExchangeRatesData || isFetching || isLoading) return;

    const nextPairKey = `${assetCodeToPay}|${assetCodeToReceive}`;
    const isPairChanged = nextPairKey !== pairKeyRef.current;
    const isRateChanged = assetsExchangeRatesData !== ratesDataRef.current;

    if (!isPairChanged && !isRateChanged) return;

    pairKeyRef.current = nextPairKey;
    ratesDataRef.current = assetsExchangeRatesData;

    const isAmountToPayEntered = Number(amountToPayRef.current) > 0;
    const isAmountToReceiveEntered = Number(amountToReceiveRef.current) > 0;

    if (isPairChanged) {
      if (isAmountToPayEntered) {
        recalculateAmountToReceive(String(amountToPayRef.current));
      } else if (isAmountToReceiveEntered) {
        recalculateAmountToPay(String(amountToReceiveRef.current));
      }
      return;
    }

    if (amountSourceRef.current === 'pay' && isAmountToPayEntered) {
      recalculateAmountToReceive(String(amountToPayRef.current));
    } else if (
      amountSourceRef.current === 'receive' &&
      isAmountToReceiveEntered
    ) {
      recalculateAmountToPay(String(amountToReceiveRef.current));
    }
  }, [
    assetCodeToPay,
    assetCodeToReceive,
    assetsExchangeRatesData,
    isFetching,
    isLoading,
    recalculateAmountToPay,
    recalculateAmountToReceive,
  ]);

  return {
    exchangeRateDescription,
    isConversionLoading: isLoading,
    isRatesFetching: isFetching,
    isRatesError,
    ratesErrorMessage: ratesError?.message ?? null,
    hasRates: !!assetsExchangeRatesData,
    recalculateAmountToReceive,
    recalculateAmountToPay,
  };
};

export { useExchangeConversion };
